import { useState } from 'react'

import { localizeHolidayName } from '../../../../../../common/holidays.js'
import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'

export interface Holiday {
    id: number
    date: string
    name: string
    company_id: number
    year: number
}

interface Company {
    id: number
    name: string
    reg_code: string | null
}

export interface HolidaysSectionProps {
    lang: Language
    holidays: Holiday[]
    year: number
    companies: Company[]
    selectedCompanyId: number
    onChangeYear: (year: number) => void
    onChangeCompany: (companyId: number) => void
    onAdd: (date: string, name: string, companyId: number) => void
    onUpdate: (id: number, date: string, name: string) => void
    onDelete: (id: number) => void
}

export const HolidaysSection = (props: HolidaysSectionProps) => {
    const { lang, holidays, year, companies, selectedCompanyId } = props
    const s = t.portal.settings.holidays
    const [date, setDate] = useState('')
    const [name, setName] = useState('')
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editDate, setEditDate] = useState('')
    const [editName, setEditName] = useState('')

    const handleAdd = () => {
        if (!date || !name) return
        props.onAdd(date, name, selectedCompanyId)
        setDate('')
        setName('')
    }

    const startEdit = (h: Holiday) => {
        setEditingId(h.id)
        setEditDate(h.date)
        // Pre-fill with the localized name so saving normalises the stored
        // (possibly English, Humaans-imported) name to Estonian.
        setEditName(localizeHolidayName(h.date, h.name, lang))
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditDate('')
        setEditName('')
    }

    const saveEdit = () => {
        if (editingId === null || !editDate || !editName) return
        props.onUpdate(editingId, editDate, editName)
        cancelEdit()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <div className="portal-settings__header-controls">
                    {companies.length > 1 ? (
                        <Select
                            className="portal-settings__input--compact"
                            value={String(selectedCompanyId)}
                            onChange={(value) => props.onChangeCompany(Number(value))}
                            options={companies.map((c) => ({
                                value: String(c.id),
                                label: c.name,
                            }))}
                        />
                    ) : null}
                    <div className="portal-settings__year-selector">
                        <button type="button" onClick={() => props.onChangeYear(year - 1)}>
                            ◀
                        </button>
                        <span>{year}</span>
                        <button type="button" onClick={() => props.onChangeYear(year + 1)}>
                            ▶
                        </button>
                    </div>
                </div>
            </div>

            <div className="portal-settings__inline-form">
                <div className="portal-settings__form-field">
                    <TextField
                        label={s.date(lang)}
                        inputProps={{ type: 'date' }}
                        value={date}
                        onChange={(value) => setDate(value)}
                    />
                </div>
                <div className="portal-settings__form-field">
                    <TextField
                        label={s.name(lang)}
                        value={name}
                        onChange={(value) => setName(value)}
                        placeholder={s.name(lang)}
                    />
                </div>
                <Button
                    variant="primary"
                    size="small"
                    text={s.save(lang)}
                    isDisabled={!date || !name}
                    onClick={handleAdd}
                />
            </div>

            {holidays.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noHolidays(lang)}</div>
            ) : (
                <div className="portal-settings__data-list">
                    {holidays.map((h) =>
                        editingId === h.id ? (
                            <div key={h.id} className="portal-settings__data-row">
                                <div className="portal-settings__data-info">
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: 8,
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <TextField
                                            inputProps={{ type: 'date', style: { width: 160 } }}
                                            value={editDate}
                                            onChange={(value) => setEditDate(value)}
                                        />
                                        <TextField
                                            inputProps={{ style: { flex: 1, minWidth: 180 } }}
                                            value={editName}
                                            onChange={(value) => setEditName(value)}
                                        />
                                    </div>
                                </div>
                                <div className="portal-settings__data-actions">
                                    <button
                                        type="button"
                                        onClick={saveEdit}
                                        disabled={!editDate || !editName}
                                    >
                                        {s.save(lang)}
                                    </button>
                                    <button type="button" onClick={cancelEdit}>
                                        {t.portal.settings.close(lang)}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div key={h.id} className="portal-settings__data-row">
                                <div className="portal-settings__data-info">
                                    <div className="portal-settings__data-name">
                                        {localizeHolidayName(h.date, h.name, lang)}
                                    </div>
                                    <div className="portal-settings__data-meta">{h.date}</div>
                                </div>
                                <div className="portal-settings__data-actions">
                                    <button type="button" onClick={() => startEdit(h)}>
                                        {s.editHoliday(lang)}
                                    </button>
                                    <button
                                        type="button"
                                        className="is-danger"
                                        onClick={() => props.onDelete(h.id)}
                                    >
                                        {s.deleteHoliday(lang)}
                                    </button>
                                </div>
                            </div>
                        ),
                    )}
                </div>
            )}
        </div>
    )
}
